import React from 'react';

import { Button } from '@components/common';
import { ConfettiBackgroundSVG } from '@components/svgs';

import { ConfirmationIcon } from '../ConfirmationIcon';

interface ConfirmationLayoutProps {
  t: (key: string) => string;
  headingKey: string;
  textKey: string;
  showIcon?: boolean;
  showConfetti?: boolean;
  actionButton?: React.ReactNode;
  participation: Participation | null;
  account: PublicGQAccount;
}

const ConfirmationLayout: React.FC<ConfirmationLayoutProps> = ({
  t,
  headingKey,
  textKey,
  showIcon = true,
  showConfetti = false,
  actionButton,
  participation,
  account
}) => (
  <div className='relative' style={{ overflow: 'visible' }}>
    {showConfetti && <ConfettiBackgroundSVG />}
    <div
      id='step-done'
      key='done'
      className='tablet:mb-48 relative z-10 my-8 rounded-sm border border-gray-200 bg-white p-10 text-center'
    >
      {showIcon && (
        <div className='confirmation-page__icon mb-6 flex justify-center'>
          <ConfirmationIcon />
        </div>
      )}
      <h2 role='alert' aria-live='assertive' className='text-custom-brand py-2 text-2xl font-bold'>
        {t(headingKey)}
      </h2>
      <div className='text-custom-brand pb-8'>{t(textKey)}</div>
      {actionButton}
      {participation?.consent_token && !account.hide_global_opt_out_candidate_ux && (
        <div className='mt-6 border-t border-gray-200 pt-6'>
          <div className='text-custom-brand pb-8 font-bold'>{t('hearing_from_us')}</div>
          <Button
            href={`/my_preferences/${participation.consent_token}`}
            noStyle
            className='btn-custom-brand-secondary'
          >
            {t('update_contact_preferences')}
          </Button>
        </div>
      )}
    </div>
  </div>
);

export const AutoDisqualified = (
  props: Omit<ConfirmationLayoutProps, 'headingKey' | 'textKey' | 'showConfetti' | 'actionButton' | 'showIcon'>
) => (
  <ConfirmationLayout
    {...props}
    headingKey='auto_disqualification.heading'
    textKey='auto_disqualification.text'
    showIcon={false}
    showConfetti={false}
  />
);

export const ManualReviewQualification = (
  props: Omit<ConfirmationLayoutProps, 'headingKey' | 'textKey' | 'showConfetti' | 'actionButton'>
) => (
  <ConfirmationLayout {...props} headingKey='manual_review.heading' textKey='manual_review.text' showConfetti={false} />
);

interface AutoQualifiedProps
  extends Omit<ConfirmationLayoutProps, 'headingKey' | 'textKey' | 'showConfetti' | 'actionButton'> {
  page: string;
  previewConfirmationType?: string;
  handleStartClick: () => void;
}
export const AutoQualified = ({
  t,
  page,
  previewConfirmationType,
  participation,
  account,
  handleStartClick
}: AutoQualifiedProps) => {
  const keyPrefix = previewConfirmationType && !participation ? 'requested_to_get_started' : page;
  return (
    <ConfirmationLayout
      t={t}
      headingKey={`${keyPrefix}.heading`}
      textKey={`${keyPrefix}.text`}
      showConfetti={true}
      participation={participation}
      account={account}
      actionButton={
        <Button onClick={handleStartClick} noStyle className='btn-custom-brand'>
          {t(`${keyPrefix}.cta`)}
        </Button>
      }
    />
  );
};
